import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { useContactForm } from "@/hooks/useContactForm";
import ContactTypeSelector from "@/components/contact/ContactTypeSelector";
import ContactInfo from "@/components/contact/ContactInfo";
import ContactSuccess from "@/components/contact/ContactSuccess";
import { 
  CommonFields, 
  IndividualFields, 
  OrganizationFields, 
  MessageField 
} from "@/components/contact/ContactFormFields";

/**
 * Contact page with Individual and Organization form options
 */
const Contact = () => {
  const {
    contactType,
    setContactType,
    individualForm,
    orgForm,
    loading,
    submitted,
    handleIndividualChange,
    handleOrgChange,
    handleSubmit,
    resetForms
  } = useContactForm();

  if (submitted) {
    return <ContactSuccess onReset={resetForms} />;
  }

  const currentForm = contactType === "individual" ? individualForm : orgForm;
  const currentHandler = contactType === "individual" ? handleIndividualChange : handleOrgChange;
  const messagePlaceholder = contactType === "individual" 
    ? "Tell us about your goals and how we can help..." 
    : "Tell us about your organisation and what you're looking for...";

  return (
    <main data-testid="contact-page" className="pt-24">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-4">
              Get in Touch
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight uppercase leading-tight mb-6">
              Contact <span className="text-[#56C6C6]">V1</span>
            </h1>
            <p className="text-xl text-zinc-400">
              Have questions about our programs? Ready to start your athletic journey? 
              We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-[#121212]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <ContactInfo />

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#0A0A0A] border border-white/5 p-8 md:p-12">
                <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-6">
                  Send Us a Message
                </h3>
                
                <ContactTypeSelector 
                  contactType={contactType} 
                  setContactType={setContactType} 
                />
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <CommonFields 
                    formData={currentForm} 
                    onChange={currentHandler} 
                  />

                  {contactType === "individual" ? (
                    <IndividualFields 
                      formData={individualForm} 
                      onChange={handleIndividualChange} 
                    />
                  ) : (
                    <OrganizationFields 
                      formData={orgForm} 
                      onChange={handleOrgChange} 
                    />
                  )}

                  <MessageField
                    value={currentForm.message}
                    onChange={currentHandler}
                    placeholder={messagePlaceholder}
                  />

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="w-full bg-[#56C6C6] text-black hover:bg-[#45B5B5] font-bold uppercase tracking-wider py-6 text-sm"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
